import { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { Heart, MapPin, Calendar, Clock, Gift, Bus, Pause, Play, Check, Copy, ExternalLink, ChevronDown, CalendarPlus, Bed, Wine, Utensils, Music2, Sparkles } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Checkbox } from '../components/ui/checkbox';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import axios from 'axios';
import FAQSection from '../components/FAQSection';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

// --- Global UI Components ---

const RichText = ({ text }) => {
  if (!text) return null;
  return text.split('\n').map((line, i) => {
    if (!line.trim()) return <br key={i} />;
    const parts = line.split(/(\*\*.*?\*\*)/);
    return (
      <div key={i} className="mb-2 last:mb-0">
        {parts.map((p, k) => {
          if (p.startsWith('**') && p.endsWith('**')) {
            return <strong key={k} className="font-bold">{p.slice(2, -2)}</strong>;
          }
          return p;
        })}
      </div>
    );
  });
};

const getActivityIcon = (title, desc) => {
  const d = (title + ' ' + (desc || '')).toLowerCase();
  if (d.includes('ceremonia') || d.includes('sí') || d.includes('altar')) return <Heart className="w-6 h-6" />;
  if (d.includes('cóctel') || d.includes('brindis') || d.includes('vino') || d.includes('aperitivo')) return <Wine className="w-6 h-6" />;
  if (d.includes('cena') || d.includes('banquete') || d.includes('comida') || d.includes('almuerzo')) return <Utensils className="w-6 h-6" />;
  if (d.includes('fiesta') || d.includes('bailar') || d.includes('baile') || d.includes('música')) return <Music2 className="w-6 h-6" />;
  if (d.includes('fin') || d.includes('pronto') || d.includes('casa')) return <Sparkles className="w-6 h-6" />;
  if (d.includes('llegada') || d.includes('bienvenida') || d.includes('recepción')) return <MapPin className="w-6 h-6" />;
  return <Sparkles className="w-6 h-6" />;
};

const AgendaItem = ({ item, pc }) => (
  <div className="flex flex-col items-center text-center relative z-10 w-full lg:w-auto">
    <div className="mb-6 px-6 py-2 rounded-full text-base font-bold text-white shadow-lg transform transition-transform hover:scale-110" style={{ backgroundColor: pc }}>
      {item.time}
    </div>
    <div className="w-20 h-20 rounded-full bg-white border-4 flex items-center justify-center mb-6 shadow-xl transition-all duration-500 hover:scale-125 hover:rotate-6 bg-wedding-background group" style={{ borderColor: pc + '40', color: pc }}>
      <div className="transition-transform group-hover:scale-110">
        {getActivityIcon(item.title, item.description)}
      </div>
    </div>
    <div className="bg-white/60 backdrop-blur-md p-6 rounded-3xl shadow-sm border border-white/50 lg:bg-transparent lg:p-0 lg:shadow-none lg:border-none">
      <h4 className="font-heading text-2xl text-wedding-text mb-2 tracking-tight">{item.title}</h4>
      <p className="text-sm text-wedding-text-muted max-w-[180px] mx-auto leading-relaxed">{item.description}</p>
    </div>
  </div>
);

const AccommodationItem = ({ opt, pc }) => (
  <div className="bg-white rounded-2xl p-8 shadow-md border border-wedding-accent/20 flex flex-col items-center text-center">
    <h3 className="font-heading text-2xl text-wedding-text mb-2">{opt.name}</h3>
    <p className="text-wedding-text-muted mb-4 text-sm">{opt.description}</p>
    <div className="flex items-center gap-2 text-wedding-text-muted mb-6">
      <MapPin className="w-4 h-4" />
      <span className="text-sm">{opt.address}</span>
    </div>
    {opt.link && (
      <Button asChild variant="outline" className="rounded-full w-full" style={{ borderColor: pc, color: pc }}>
        <a href={opt.link} target="_blank" rel="noopener noreferrer">
          <ExternalLink className="w-4 h-4 mr-2" />
          Ver sitio web
        </a>
      </Button>
    )}
  </div>
);

// --- Sections ---

const HeroSection = ({ event, countdown, scrollToRsvp }) => {
  const videoUrl = event?.hero_video;
  const isYouTube = videoUrl?.includes('youtube.com') || videoUrl?.includes('youtu.be');

  const getYouTubeId = (url) => {
    const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|\&v=)([^#\&\?]*).*/;
    const match = url?.match(regExp);
    return (match && match[2].length === 11) ? match[2] : null;
  };

  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
      <div className="absolute inset-0 z-0">
        {videoUrl ? (
          <>
            {isYouTube ? (
              <iframe
                className="absolute w-[300%] h-[300%] top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 pointer-events-none"
                src={`https://www.youtube.com/embed/${getYouTubeId(videoUrl)}?autoplay=1&mute=1&controls=0&loop=1&playlist=${getYouTubeId(videoUrl)}&rel=0&showinfo=0&iv_load_policy=3&disablekb=1`}
                allow="autoplay; encrypted-media"
                frameBorder="0"
              />
            ) : (
              <video
                autoPlay
                muted
                loop
                playsInline
                className="absolute w-full h-full object-cover"
              >
                <source src={videoUrl} type="video/mp4" />
              </video>
            )}
          </>
        ) : (
          <div
            className="absolute inset-0 bg-cover bg-center"
            style={{ backgroundImage: 'url(' + ((event && event.hero_image) || 'https://images.unsplash.com/photo-1761211488173-a7154314420a?w=1920&q=80') + ')' }}
          />
        )}
      </div>

      <div
        className="absolute inset-0 bg-black z-[1]"
        style={{ opacity: event?.hero_overlay_opacity ?? 0.4 }}
      />

      <div className="relative z-10 text-center px-6 max-w-4xl">
        <p className="text-white/80 uppercase tracking-widest text-sm mb-4">{(event && event.slogan) || 'Tenemos el honor de invitarte'}</p>
        <h1 className="font-heading text-5xl md:text-7xl text-white mb-6">{event && event.title}</h1>
        {event && event.description && (
          <p
            className="text-white/90 mb-10 max-w-2xl mx-auto"
            style={{
              fontFamily: event.description_font || 'inherit',
              fontSize: (event.description_font_size || 18) + 'px'
            }}
          >
            {event.description}
          </p>
        )}
        <div className="mt-8 grid grid-cols-4 gap-4 max-w-lg mx-auto">
          {[{ v: countdown.days, l: 'Días' }, { v: countdown.hours, l: 'Horas' }, { v: countdown.minutes, l: 'Min' }, { v: countdown.seconds, l: 'Seg' }].map((x, i) => (
            <div key={i} className="bg-white/20 backdrop-blur-md rounded-xl p-4">
              <p className="text-3xl font-heading text-white">{x.v}</p>
              <p className="text-white/70 text-xs uppercase">{x.l}</p>
            </div>
          ))}
        </div>
      </div>
      <div className="absolute bottom-10 left-1/2 -translate-x-1/2 text-center cursor-pointer z-10" onClick={scrollToRsvp}>
        <p className="text-white/80 text-sm uppercase tracking-widest mb-2">Confirma tu asistencia</p>
        <ChevronDown className="w-6 h-6 text-white/60 mx-auto animate-bounce" />
      </div>
    </section>
  );
};

const DetailsSection = ({ event, pc, bg, formatDate, addToCalendar }) => (
  <section className="py-24 px-6" style={{ backgroundColor: bg }}>
    <div className="max-w-4xl mx-auto">
      <div className="text-center mb-16">
        <p className="uppercase tracking-widest text-sm mb-4" style={{ color: pc }}>Detalles</p>
        <h2 className="font-heading text-4xl text-wedding-text">Cuándo y Dónde</h2>
      </div>
      <div className="grid md:grid-cols-2 gap-8">
        <div className="bg-white/80 backdrop-blur-md rounded-2xl p-8 shadow-lg">
          <div className="flex items-center gap-4 mb-4">
            <div className="w-12 h-12 rounded-full flex items-center justify-center transition-transform hover:scale-110" style={{ backgroundColor: pc + '20' }}>
              <Calendar className="w-6 h-6" style={{ color: pc }} />
            </div>
            <h3 className="font-heading text-xl text-wedding-text">Fecha</h3>
          </div>
          <p className="text-lg capitalize text-wedding-text">{formatDate(event && event.event_date)}</p>
          <p className="mt-2 flex items-center gap-2 text-wedding-text-muted"><Clock className="w-4 h-4" />{event && event.event_time}</p>
          <Button onClick={addToCalendar} variant="outline" className="mt-4 rounded-full text-sm" style={{ borderColor: pc, color: pc }}>
            <CalendarPlus className="w-4 h-4 mr-2" />Añadir a Calendar
          </Button>
        </div>
        <div className="bg-white/80 backdrop-blur-md rounded-2xl p-8 shadow-lg">
          <div className="flex items-center gap-4 mb-4">
            <div className="w-12 h-12 rounded-full flex items-center justify-center transition-transform hover:scale-110" style={{ backgroundColor: pc + '20' }}>
              <MapPin className="w-6 h-6" style={{ color: pc }} />
            </div>
            <h3 className="font-heading text-xl text-wedding-text">Lugar</h3>
          </div>
          <p className="text-lg text-wedding-text">{event && event.venue_name}</p>
          <p className="text-wedding-text-muted">{event && event.venue_address}</p>
          {event && event.google_maps_link && (
            <a href={event.google_maps_link} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 mt-4 text-sm font-semibold hover:underline" style={{ color: pc }}>
              <ExternalLink className="w-4 h-4" />Ver en Maps
            </a>
          )}
        </div>
      </div>
      {event && event.dress_code && (
        <div className="text-center mt-12 p-8 rounded-2xl bg-wedding-accent/20 border border-wedding-accent">
          <p className="text-sm uppercase tracking-widest text-wedding-text-muted mb-3">Código de vestimenta</p>
          <p className="font-heading text-2xl text-wedding-text italic">{event.dress_code}</p>
        </div>
      )}
    </div>
  </section>
);

const AgendaSection = ({ event, pc, bg }) => (
  <section className="py-24 px-6 overflow-hidden" style={{ backgroundColor: bg }}>
    <div className="max-w-6xl mx-auto">
      <div className="text-center mb-16">
        <h2 className="font-heading text-5xl text-wedding-text mb-4">Programa del día</h2>
        <p className="text-wedding-text-muted">Nuestra celebración paso a paso</p>
      </div>
      <div className="relative mt-20">
        <div className="absolute top-[40px] left-[10%] w-[80%] h-1 bg-wedding-accent/50 hidden lg:block" />
        <div className="absolute top-0 left-1/2 w-1 h-full bg-wedding-accent/50 -translate-x-1/2 lg:hidden" />
        <div className="flex flex-col lg:flex-row justify-between items-center gap-24 lg:gap-4 relative">
          {(event.agenda || []).map((item, i) => (
            <AgendaItem key={i} item={item} pc={pc} />
          ))}
        </div>
      </div>
    </div>
  </section>
);

const QuoteSection = ({ event }) => {
  if (!event || !event.quote_enabled || !event.quote_text) return null;
  return (
    <section className="relative py-32 px-6 flex items-center justify-center overflow-hidden">
      {event.quote_bg_image ? (
        <div
          className="absolute inset-0 bg-cover bg-center z-0"
          style={{ backgroundImage: `url(${event.quote_bg_image})` }}
        />
      ) : (
        <div className="absolute inset-0 z-0 bg-wedding-accent/10" />
      )}
      <div className="absolute inset-0 bg-black/40 z-[1]" />
      <div className="relative z-10 max-w-4xl text-center">
        <Sparkles className="w-8 h-8 text-white/60 mx-auto mb-8 animate-pulse" />
        <blockquote className="space-y-6">
          <p className="font-heading text-3xl md:text-5xl text-white italic leading-tight">
            "{event.quote_text}"
          </p>
          {event.quote_author && (
            <footer className="text-white/80 uppercase tracking-[0.3em] text-sm md:text-base mt-4">
              — {event.quote_author} —
            </footer>
          )}
        </blockquote>
      </div>
    </section>
  );
};

const AccommodationSection = ({ event, pc }) => (
  <section className="py-24 px-6 bg-wedding-accent/10">
    <div className="max-w-4xl mx-auto">
      <div className="text-center mb-16">
        <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6 transition-transform hover:rotate-12" style={{ backgroundColor: pc + '20' }}>
          <Bed className="w-8 h-8" style={{ color: pc }} />
        </div>
        <h2 className="font-heading text-4xl text-wedding-text mb-4">Alojamiento</h2>
        <p className="text-wedding-text-muted max-w-2xl mx-auto">{event.accommodation_text}</p>
      </div>
      <div className="grid md:grid-cols-2 gap-6">
        {(event.accommodation_options || []).map((opt, i) => (
          <AccommodationItem key={i} opt={opt} pc={pc} />
        ))}
      </div>
    </div>
  </section>
);

const GiftsSection = ({ event, pc }) => {
  const [showAccounts, setShowAccounts] = useState(false);

  return (
    <section className="py-24 px-6 bg-wedding-accent/20">
      <div className="max-w-4xl mx-auto text-center">
        <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6 transition-transform hover:-rotate-12" style={{ backgroundColor: pc + '20' }}>
          <Gift className="w-8 h-8" style={{ color: pc }} />
        </div>
        <h2 className="font-heading text-4xl text-wedding-text mb-8">Lista de Regalos</h2>

        {event.gifts_text && (
          <div className="mb-12 text-wedding-text-muted text-lg leading-relaxed max-w-2xl mx-auto">
            <RichText text={event.gifts_text} />
          </div>
        )}

        <div className="mb-12">
          <Button
            onClick={() => setShowAccounts(!showAccounts)}
            className="rounded-full px-8 py-6 text-lg transition-all shadow-md hover:shadow-xl active:scale-95"
            style={{ backgroundColor: pc }}
          >
            {showAccounts ? 'Ocultar datos' : 'Ver datos bancarios'}
            <ChevronDown className={`ml-2 w-5 h-5 transition-transform duration-500 ${showAccounts ? 'rotate-180' : ''}`} />
          </Button>
        </div>

        {showAccounts && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 animate-in fade-in slide-in-from-top-10 duration-700">
            {(event.bank_accounts || []).map((acc, i) => (
              <div
                key={i}
                className="group relative bg-white rounded-3xl p-8 shadow-xl border-t-8 transition-all hover:scale-[1.02]"
                style={{ borderTopColor: acc.color || pc }}
              >
                <div className="flex justify-between items-start mb-6">
                  <div className="text-left">
                    <p className="text-[10px] uppercase tracking-[0.2em] text-wedding-text-muted mb-1">Banco</p>
                    <h3 className="font-heading text-2xl text-wedding-text">{acc.bank_name || 'Banco'}</h3>
                  </div>
                  {acc.bank_logo && (
                    <img src={acc.bank_logo} alt={acc.bank_name} className="h-10 w-auto object-contain" />
                  )}
                </div>

                <div className="space-y-4 text-left">
                  <div className="flex justify-between items-end">
                    <div>
                      <p className="text-[10px] uppercase tracking-widest text-wedding-text-muted mb-1">Número de Cuenta</p>
                      <p className="font-mono text-lg font-bold text-wedding-text break-all tracking-wider">{acc.account_number}</p>
                    </div>
                    {acc.account_type && (
                      <div className="text-right">
                        <p className="text-[10px] uppercase tracking-widest text-wedding-text-muted mb-1">Tipo</p>
                        <p className="text-xs font-semibold px-2 py-0.5 bg-gray-100 rounded text-wedding-text">{acc.account_type}</p>
                      </div>
                    )}
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-[10px] uppercase tracking-widest text-wedding-text-muted mb-1">Titular</p>
                      <p className="text-sm font-semibold text-wedding-text">{acc.holder_name}</p>
                    </div>
                    {acc.id_number && (
                      <div>
                        <p className="text-[10px] uppercase tracking-widest text-wedding-text-muted mb-1">Cédula / CI</p>
                        <p className="text-sm font-semibold text-wedding-text">{acc.id_number}</p>
                      </div>
                    )}
                  </div>

                  {acc.email && (
                    <div>
                      <p className="text-[10px] uppercase tracking-widest text-wedding-text-muted mb-1">Correo</p>
                      <p className="text-sm font-medium text-wedding-text">{acc.email}</p>
                    </div>
                  )}
                </div>

                <Button
                  onClick={() => {
                    const text = `Banco: ${acc.bank_name}\nCuenta (${acc.account_type}): ${acc.account_number}\nTitular: ${acc.holder_name}\nID: ${acc.id_number}\nCorreo: ${acc.email}`;
                    navigator.clipboard.writeText(text);
                    toast.success('Datos copiados');
                  }}
                  variant="outline"
                  className="mt-8 w-full rounded-full border-2 transition-colors hover:text-white"
                  style={{ borderColor: acc.color || pc, color: acc.color || pc }}
                >
                  <Copy className="w-4 h-4 mr-2" />Copiar datos
                </Button>
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

const RSVPFormSection = ({ event, pc, familyToken, family, submitted, submitting, handleSubmitFamilyRSVP, guests, rsvpData, handleRSVPChange, needsTransport, setNeedsTransport, publicRsvp, setPublicRsvp, handleSubmitPublicRSVP, handleAllergyToggle, allergiesList }) => (
  <section id="rsvp" className="py-24 px-6" style={{ backgroundColor: pc + '10' }}>
    <div className="max-w-2xl mx-auto">
      <div className="text-center mb-12">
        <p className="uppercase tracking-widest text-sm mb-4" style={{ color: pc }}>RSVP</p>
        <h2 className="font-heading text-4xl text-wedding-text mb-4">{(event && event.rsvp_title) || 'Esperamos contar contigo'}</h2>
        {familyToken && family && <p className="font-accent text-3xl" style={{ color: pc }}>{family.name}</p>}
      </div>
      <div className="bg-white/90 backdrop-blur-md rounded-2xl p-8 shadow-xl">
        {submitted ? (
          <div className="text-center py-8">
            <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6" style={{ backgroundColor: pc + '20' }}>
              <Check className="w-8 h-8" style={{ color: pc }} />
            </div>
            <h3 className="font-heading text-2xl text-wedding-text mb-2">¡Gracias por confirmar!</h3>
            <p className="text-wedding-text-muted">¡Nos vemos pronto!</p>
          </div>
        ) : familyToken && family ? (
          <div>
            <div className="space-y-4 mb-8">
              <p className="font-semibold text-wedding-text mb-4">Selecciona quién asistirá:</p>
              {guests.map(g => (
                <div key={g.id} className="flex flex-col sm:flex-row items-center justify-between p-4 rounded-xl bg-wedding-accent/20 gap-4">
                  <Label htmlFor={'g-' + g.id} className="text-lg font-medium text-wedding-text flex-1 text-center sm:text-left">
                    {g.first_name} {g.last_name}
                  </Label>
                  <RadioGroup
                    value={rsvpData[g.id] === true ? 'yes' : rsvpData[g.id] === false ? 'no' : ''}
                    onValueChange={v => handleRSVPChange(g.id, v === 'yes')}
                    className="flex gap-2"
                  >
                    <div className="flex items-center">
                      <RadioGroupItem value="yes" id={`yes-${g.id}`} className="sr-only" />
                      <Label
                        htmlFor={`yes-${g.id}`}
                        className={`px-4 py-2 rounded-full text-sm cursor-pointer transition-all ${rsvpData[g.id] === true ? 'text-white shadow-md' : 'bg-white/50 text-wedding-text-muted hover:bg-white'}`}
                        style={rsvpData[g.id] === true ? { backgroundColor: pc } : {}}
                      >
                        Asistirá
                      </Label>
                    </div>
                    <div className="flex items-center">
                      <RadioGroupItem value="no" id={`no-${g.id}`} className="sr-only" />
                      <Label
                        htmlFor={`no-${g.id}`}
                        className={`px-4 py-2 rounded-full text-sm cursor-pointer transition-all ${rsvpData[g.id] === false ? 'bg-red-500 text-white shadow-md' : 'bg-white/50 text-wedding-text-muted hover:bg-white'}`}
                      >
                        No asistirá
                      </Label>
                    </div>
                  </RadioGroup>
                </div>
              ))}
            </div>
            {event && event.transport_enabled && (
              <div className="p-4 rounded-xl bg-wedding-accent/20 mb-8">
                <div className="flex items-center gap-3">
                  <Checkbox id="transport" checked={needsTransport} onCheckedChange={setNeedsTransport} />
                  <Label htmlFor="transport" className="cursor-pointer text-wedding-text"><Bus className="w-4 h-4 inline mr-2" />Necesitamos transporte</Label>
                </div>
              </div>
            )}
            <Button
              onClick={handleSubmitFamilyRSVP}
              disabled={submitting || guests.some(g => rsvpData[g.id] === undefined)}
              className="w-full rounded-full py-6 uppercase tracking-widest"
              style={{ backgroundColor: pc, color: 'white' }}
            >
              {submitting ? 'Enviando...' : 'Enviar confirmación'}
            </Button>
          </div>
        ) : (
          <form onSubmit={handleSubmitPublicRSVP} className="space-y-6">
            <div><Label>Nombre completo *</Label><Input value={publicRsvp.full_name} onChange={e => setPublicRsvp({ ...publicRsvp, full_name: e.target.value })} /></div>
            {(event && event.rsvp_show_email !== false) && <div><Label>Email (opcional)</Label><Input type="email" value={publicRsvp.email} onChange={e => setPublicRsvp({ ...publicRsvp, email: e.target.value })} /></div>}
            <div>
              <Label>¿Asistirás? *</Label>
              <RadioGroup value={publicRsvp.attending === true ? 'yes' : publicRsvp.attending === false ? 'no' : ''} onValueChange={v => setPublicRsvp({ ...publicRsvp, attending: v === 'yes' })} className="flex flex-col gap-3">
                <div className="flex items-center space-x-3 p-4 rounded-xl bg-wedding-accent/20"><RadioGroupItem value="yes" id="yes" /><Label htmlFor="yes" className="cursor-pointer text-wedding-text flex-1">Sí, asistiré</Label></div>
                <div className="flex items-center space-x-3 p-4 rounded-xl bg-wedding-accent/20"><RadioGroupItem value="no" id="no" /><Label htmlFor="no" className="cursor-pointer text-wedding-text flex-1">No podré asistir</Label></div>
              </RadioGroup>
            </div>
            {publicRsvp.attending && (event && event.rsvp_show_num_guests !== false) && (
              <div>
                <Label>Número de invitados</Label>
                <Select value={String(publicRsvp.num_guests)} onValueChange={v => setPublicRsvp({ ...publicRsvp, num_guests: parseInt(v) })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{[1, 2, 3, 4, 5].map(n => <SelectItem key={n} value={String(n)}>{n}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            )}
            {publicRsvp.attending && (event && event.rsvp_show_allergies !== false) && (
              <div>
                <Label>Alergias</Label>
                <div className="grid grid-cols-2 gap-2 mt-2">
                  {allergiesList.map(a => <div key={a.id} className="flex items-center space-x-2 p-2 rounded bg-wedding-accent/10"><Checkbox id={a.id} checked={(publicRsvp.allergies || []).includes(a.id)} onCheckedChange={() => handleAllergyToggle(a.id)} /><Label htmlFor={a.id} className="text-sm cursor-pointer">{a.label}</Label></div>)}
                </div>
                <Input className="mt-2" value={publicRsvp.other_allergies} onChange={e => setPublicRsvp({ ...publicRsvp, other_allergies: e.target.value })} placeholder="Otras alergias..." />
              </div>
            )}
            {(event && event.rsvp_show_message !== false) && <div><Label>Mensaje (opcional)</Label><Textarea value={publicRsvp.message} onChange={e => setPublicRsvp({ ...publicRsvp, message: e.target.value })} rows={3} /></div>}
            <Button type="submit" disabled={submitting} className="w-full rounded-full py-6 uppercase tracking-widest" style={{ backgroundColor: pc, color: 'white' }}>
              {submitting ? 'Enviando...' : 'Enviar confirmación'}
            </Button>
          </form>
        )}
      </div>
    </div>
  </section>
);

const FooterSection = ({ event, pc }) => (
  <footer className="py-16 px-6 text-center" style={{ backgroundColor: pc }}>
    <Heart className="w-8 h-8 mx-auto mb-6 text-white" fill="currentColor" />
    {event && event.footer_message && <p className="font-heading text-2xl text-white/90 mb-6">{event.footer_message}</p>}
    <div className="flex justify-center gap-6 text-white/70">
      {event && event.footer_phone && <a href={'tel:' + event.footer_phone}>{event.footer_phone}</a>}
    </div>
  </footer>
);

// --- Main Page Component ---

export default function PublicInvitationPage() {
  const params = useParams();
  const eventToken = params.eventToken;
  const familyToken = params.familyToken;
  const [event, setEvent] = useState(null);
  const [family, setFamily] = useState(null);
  const [guests, setGuests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [rsvpData, setRsvpData] = useState({});
  const [needsTransport, setNeedsTransport] = useState(false);
  const [publicRsvp, setPublicRsvp] = useState({ full_name: '', email: '', attending: null, num_guests: 1, allergies: [], other_allergies: '', message: '' });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const audioRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [countdown, setCountdown] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0 });

  useEffect(() => {
    if (event) {
      document.title = event.title || 'Invitación de Boda';

      const metas = {
        'og:title': event.title,
        'og:description': event.slogan || event.description,
        'og:image': event.og_image,
        'og:type': 'website',
        'twitter:card': 'summary_large_image',
        'twitter:image': event.og_image,
      };

      Object.entries(metas).forEach(([name, content]) => {
        if (!content) return;
        let meta = document.querySelector(`meta[property="${name}"]`) || document.querySelector(`meta[name="${name}"]`);
        if (!meta) {
          meta = document.createElement('meta');
          if (name.startsWith('og:')) meta.setAttribute('property', name);
          else meta.setAttribute('name', name);
          document.head.appendChild(meta);
        }
        meta.setAttribute('content', content);
      });
    }
  }, [event]);

  const allergiesList = [
    { id: 'gluten', label: 'Sin gluten / Celíaco' },
    { id: 'lactose', label: 'Sin lactosa' },
    { id: 'vegetarian', label: 'Vegetariano' },
    { id: 'vegan', label: 'Vegano' },
    { id: 'nuts', label: 'Alergia a frutos secos' },
    { id: 'seafood', label: 'Alergia a mariscos' }
  ];

  useEffect(() => {
    if (familyToken) {
      axios.get(API + '/invitation/' + eventToken + '/family/' + familyToken).then(r => {
        setEvent(r.data.event); setFamily(r.data.family); setGuests(r.data.guests);
        const rd = {}; r.data.guests.forEach(g => { rd[g.id] = g.status === 'confirmed'; }); setRsvpData(rd);
        setNeedsTransport(r.data.family.needs_transport); setSubmitted(r.data.family.status !== 'pending');
      }).catch(() => setError('Invitación no encontrada')).finally(() => setLoading(false));
    } else {
      axios.get(API + '/invitation/' + eventToken).then(r => setEvent(r.data)).catch(() => setError('Invitación no encontrada')).finally(() => setLoading(false));
    }
  }, [eventToken, familyToken]);

  useEffect(() => {
    if (event && event.music_url && event.music_autoplay !== false && audioRef.current) {
      audioRef.current.play().then(() => setIsPlaying(true)).catch(() => { });
    }
  }, [event]);

  useEffect(() => {
    if (!event || !event.event_date) return;
    const update = () => {
      const d = new Date(event.event_date + 'T' + (event.event_time || '00:00')) - new Date();
      if (d > 0) setCountdown({ days: Math.floor(d / 86400000), hours: Math.floor((d / 3600000) % 24), minutes: Math.floor((d / 60000) % 60), seconds: Math.floor((d / 1000) % 60) });
    };
    update(); const i = setInterval(update, 1000); return () => clearInterval(i);
  }, [event]);

  const toggleMusic = () => { if (audioRef.current) { isPlaying ? audioRef.current.pause() : audioRef.current.play(); setIsPlaying(!isPlaying); } };
  const scrollToRsvp = () => document.getElementById('rsvp')?.scrollIntoView({ behavior: 'smooth' });
  const handleRSVPChange = (gid, att) => setRsvpData(p => ({ ...p, [gid]: att }));
  const handleAllergyToggle = (aid) => { const c = publicRsvp.allergies || []; setPublicRsvp({ ...publicRsvp, allergies: c.includes(aid) ? c.filter(a => a !== aid) : [...c, aid] }); };

  const handleSubmitFamilyRSVP = () => {
    setSubmitting(true);
    axios.post(API + '/rsvp/' + familyToken, { guests: Object.keys(rsvpData).map(gid => ({ guest_id: gid, attending: rsvpData[gid] })), needs_transport: needsTransport })
      .then(() => { setSubmitted(true); toast.success('¡Gracias!'); }).catch(() => toast.error('Error')).finally(() => setSubmitting(false));
  };

  const handleSubmitPublicRSVP = (e) => {
    e.preventDefault();
    if (!publicRsvp.full_name) { toast.error('Ingresa tu nombre'); return; }
    if (publicRsvp.attending === null) { toast.error('Indica si asistirás'); return; }
    setSubmitting(true);
    axios.post(API + '/rsvp/public/' + eventToken, publicRsvp).then(() => { setSubmitted(true); toast.success('¡Gracias!'); }).catch(() => toast.error('Error')).finally(() => setSubmitting(false));
  };

  const copyBankDetails = () => { navigator.clipboard.writeText((event.gifts_bank_holder || '') + '\n' + (event.gifts_bank_iban || '')); toast.success('Copiado'); };
  const addToCalendar = () => { if (!event) return; const s = event.event_date.replace(/-/g, '') + 'T' + (event.event_time || '00:00').replace(/:/g, '') + '00'; window.open('https://calendar.google.com/calendar/render?action=TEMPLATE&text=' + encodeURIComponent(event.title) + '&dates=' + s + '/' + s + '&location=' + encodeURIComponent(event.venue_name), '_blank'); };
  const getTemplateStyles = () => {
    const template = event?.design_template || 'elegant';
    switch (template) {
      case 'modern':
        return { fontHeading: "'Montserrat', sans-serif", fontBody: "'Open Sans', sans-serif", borderRadius: '0px' };
      case 'romantic':
        return { fontHeading: "'Great Vibes', cursive", fontBody: "'Lato', sans-serif", borderRadius: '40px' };
      default: // elegant
        return { fontHeading: "'Playfair Display', serif", fontBody: "'Manrope', sans-serif", borderRadius: '24px' };
    }
  };

  const formatDate = (d) => {
    if (!d) return '';
    const localDate = new Date(d.replace(/-/g, '/'));
    return localDate.toLocaleDateString('es-ES', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-wedding-background"><Heart className="w-12 h-12 text-wedding-primary animate-pulse" /></div>;
  if (error) return <div className="min-h-screen flex items-center justify-center bg-wedding-background text-center"><Heart className="w-16 h-16 mx-auto mb-4 text-wedding-secondary" /><h1 className="font-heading text-2xl text-wedding-text">{error}</h1></div>;

  const pc = (event && event.colors && event.colors.primary) || '#586857';
  const sc = (event && event.colors && event.colors.secondary) || '#D4C5B0';
  const bg = (event && event.colors && event.colors.background) || '#FAFAF9';
  const tc = (event && event.colors && event.colors.text) || '#2D2D2D';
  const ts = getTemplateStyles();

  return (
    <div className="min-h-screen" style={{
      backgroundColor: bg,
      color: tc,
      '--wedding-primary': pc,
      '--wedding-secondary': sc,
      '--wedding-text': tc,
      '--font-heading': ts.fontHeading,
      '--font-body': ts.fontBody,
      '--radius-wedding': ts.borderRadius
    }}>
      <style>{`
        .font-heading { font-family: var(--font-heading) !important; }
        body { font-family: var(--font-body) !important; }
        .rounded-2xl, .rounded-3xl, .rounded-xl { border-radius: var(--radius-wedding) !important; }
      `}</style>
      {event && event.music_url && <audio ref={audioRef} loop><source src={event.music_url} type="audio/mpeg" /></audio>}
      {event && event.music_url && (
        <div className="fixed bottom-6 right-6 z-40">
          <Button size="icon" variant="outline" onClick={toggleMusic} className="rounded-full bg-white/80 shadow-lg" style={{ borderColor: pc }}>
            {isPlaying ? <Pause className="w-4 h-4" style={{ color: pc }} /> : <Play className="w-4 h-4" style={{ color: pc }} />}
          </Button>
        </div>
      )}

      <HeroSection event={event} countdown={countdown} scrollToRsvp={scrollToRsvp} />

      <DetailsSection event={event} pc={pc} bg={bg} formatDate={formatDate} addToCalendar={addToCalendar} />

      <QuoteSection event={event} />

      {event && event.agenda && event.agenda.length > 0 && (
        <AgendaSection event={event} pc={pc} bg={bg} />
      )}

      {event && event.accommodation_enabled && (
        <AccommodationSection event={event} pc={pc} />
      )}

      {event && event.gifts_enabled && (
        <GiftsSection
          event={event}
          pc={pc}
        />
      )}

      {event && event.transport_enabled && (
        <section className="py-24 px-6" style={{ backgroundColor: bg }}>
          <div className="max-w-4xl mx-auto text-center">
            <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6" style={{ backgroundColor: pc + '20' }}>
              <Bus className="w-8 h-8" style={{ color: pc }} />
            </div>
            <h2 className="font-heading text-4xl text-wedding-text mb-8">Transporte</h2>
            {event.transport_text && <p className="mb-8 text-wedding-text-muted max-w-2xl mx-auto">{event.transport_text}</p>}
          </div>
        </section>
      )}

      {event && event.faqs_enabled && (
        <FAQSection event={event} pc={pc} bg={bg} />
      )}

      <RSVPFormSection
        event={event} pc={pc} familyToken={familyToken} family={family}
        submitted={submitted} submitting={submitting} handleSubmitFamilyRSVP={handleSubmitFamilyRSVP}
        guests={guests} rsvpData={rsvpData} handleRSVPChange={handleRSVPChange}
        needsTransport={needsTransport} setNeedsTransport={setNeedsTransport}
        publicRsvp={publicRsvp} setPublicRsvp={setPublicRsvp} handleSubmitPublicRSVP={handleSubmitPublicRSVP}
        handleAllergyToggle={handleAllergyToggle} allergiesList={allergiesList}
      />

      <FooterSection event={event} pc={pc} />
    </div>
  );
}
